import React from "react"
import { RadioGroup } from "@headlessui/react"
import { CheckCircleIcon } from "@heroicons/react/24/solid"
import { BanknotesIcon, CreditCardIcon, LinkIcon, NoSymbolIcon } from "@heroicons/react/20/solid"
import classNames from "../../../utils/classNames"
import ePaymentType from "../../../enums/ePaymentType"

interface ComponentProps {
  label?: string
  options: Array<string>
  selectedOption: any
  onChange?: any
  isDeposit?: boolean
}

const PaymentTypeSelector = (props: ComponentProps) => {
  const normalizeOption = (option: string) => {
    if (option === ePaymentType.CARD_READER) {
      return ePaymentType.CARD_INPUT
    }
    return option
  }

  return (
    <RadioGroup
      className="mb-4"
      value={normalizeOption(props.selectedOption)}
      onChange={(value) => props.onChange(value)}
    >
      <RadioGroup.Label className="text-base text-gray-900">{props.label}</RadioGroup.Label>

      <div className="gap-2 grid grid-cols-2 row-span-2 sm:flex sm:flex-row sm:flex-wrap">
        {props.options.map((option, index) => (
          <RadioGroup.Option
            key={index}
            value={option}
            disabled={props.isDeposit && option == ePaymentType.CASH_UNPAID}
            className={({ checked, active, disabled }) =>
              classNames(
                checked ? "border-transparent" : "border-gray-300",
                active ? "ring-1 ring-green-600" : "",
                disabled ? "cursor-not-allowed" : "cursor-pointer",
                "flex-grow relative bg-white border rounded-lg shadow-sm p-2 flex focus:outline-none",
              )
            }
          >
            {({ checked, active }) => (
              <>
                <div className="flex-grow flex justify-start">
                  <div className={classNames("flex items-center text-center justify-items-center mt-0.5")}>
                    <Icon
                      name={String(option)}
                      className={classNames(
                        "w-5 h-5 self-center ml-0",
                        checked ? "text-green-600" : "text-gray-900",
                        props.isDeposit && option == ePaymentType.CASH_UNPAID && "!text-gray-300",
                      )}
                    />
                    <RadioGroup.Label
                      as="span"
                      className={classNames(
                        "flex text-sm ml-2 leading-tight",
                        checked ? "text-green-600 font-semibold" : "text-gray-900 font-medium",
                        props.isDeposit && option == ePaymentType.CASH_UNPAID && "!text-gray-300",
                      )}
                    >
                      {Label(String(option))}
                    </RadioGroup.Label>
                  </div>
                </div>
                <div
                  className={classNames(
                    active ? "border" : "border-2",
                    checked ? "border-green-600" : "border-transparent",
                    "absolute -inset-px rounded-lg pointer-events-none",
                  )}
                  aria-hidden="true"
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  )
}

const Icon = ({ name, className }: { name: string; className: string }) => {
  switch (name) {
    case ePaymentType.CARD_INPUT:
      return <CreditCardIcon className={className} />
    case ePaymentType.LINK:
      return <LinkIcon className={className} />
    case "cash":
      return <BanknotesIcon className={className} />
    case ePaymentType.CASH_PAID:
      return <BanknotesIcon className={className} />
    case ePaymentType.CASH_UNPAID:
      return <NoSymbolIcon className={className} />
    default:
      console.error("Missing icon for:", name)
      return <svg />
  }
}

const Label = (name: string) => {
  switch (name) {
    case ePaymentType.CARD_INPUT:
      return "Credit card"
    case ePaymentType.LINK:
      return "Invoice"
    case "cash":
      return "Paid"
    case ePaymentType.CASH_PAID:
      return "Paid offline"
    case ePaymentType.CASH_UNPAID:
      return "Unpaid"
    default:
      break
  }
}

export default PaymentTypeSelector
